<template>
  <div
      class="editorPanel backgroundPanel tw-left-2 tw-right-2 lg:tw-left-auto tw-bg-white tw-absolute tw-bottom-0 tw-transition tw-ease-out tw-duration-500 tw-rounded-t-[24px] lg:tw-rounded-tr-none lg:tw-rounded-l-[24px] lg:tw-w-[400px] lg:tw-right-0 lg:tw-bottom-auto lg:tw-top-1/2 lg:tw-translate-y-[-50%] tw-font-sans tw-z-[2] tw-overflow-hidden"
      :class="{ panelHidden: !showBackgroundPanel }"
      :style="panelStyle"
  >
    <div ref="panelContentRef" class="tw-flex tw-flex-col tw-overflow-y-auto">
    <div class="textOptions tw-flex tw-justify-between tw-items-center tw-p-4 tw-px-8">
      <img
          :src="getAssetPath('/assets/images/collapseIcon.svg')"
          :alt="$t('closeButton')"
          @click="$emit('showBackgroundPanel', false)"
          class="tw-cursor-pointer"
      />
      <div
          class="tw-flex tw-justify-start tw-items-center tw-w-full tw-rounded-l-full tw-rounded-r-full tw-bg-bgDark tw-border-borderLight20 tw-border-1 tw-border-solid tw-ml-4"
      >
        <div
            :style="{ backgroundColor: color }"
            class="tw-border-none tw-text-black tw-rounded-l-full tw-rounded-r-full tw-font-medium tw-text-xl tw-p-2 tw-w-6 tw-h-6 tw-ml-1 tw-mt-1 tw-mb-1"
        ></div>
        <div class="tw-ml-[-2rem] tw-flex-1 tw-flex tw-justify-center tw-font-sans">
          <input
              class="tw-bg-transparent tw-border-none tw-text-xl tw-text-center tw-outline-none tw-text-white"
              style="padding: 0; width: auto"
              type="text"
              v-model="color"
              @input="onColorChange"
          />
        </div>
      </div>
    </div>

    <div class="tw-px-8">
      <div class="tw-flex tw-flex-col tw-items-center tw-justify-center">
        <ColorPicker
            v-model="color"
            @update:model-value="onColorChange"
            :disableAlpha="true"
            :disableFields="true"
        />
      </div>

      <div
          class="tw-overflow-x-auto tw-whitespace-nowrap backgroundOptions tw-rounded-l-full tw-rounded-r-full tw-bg-bgDark tw-border-borderLight20 tw-border-1 tw-border-solid tw-mb-4"
          :class="scrollbarClasses"
      >
        <span
            v-for="(col, i) in cardColors"
            :key="i"
            class="cardColor"
            :class="{ activeColor: isSameColor(col, color) }"
            :style="'background-color: ' + col"
            @click.stop="onPresetColorClick(col)"
        ></span>
      </div>

      <div
          class="tw-rounded-l-full tw-rounded-r-full tw-bg-bgDark tw-border-solYellow tw-border-1 tw-border-solid tw-flex tw-justify-center tw-items-center tw-text-xl tw-p-4 tw-mb-4 tw-text-white tw-font-semibold tw-cursor-pointer"
          @click.stop="$emit('onBackgroundImageUpload')"
      >
        <span class="tw-flex tw-justify-center tw-items-center">
          <img
              :src="getAssetPath('/assets/images/uploadBackground.svg')"
              :alt="$t('uploadBackground')"
              class="tw-mr-2"
          />
          {{ $t("uploadBackground") }}
        </span>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { Chrome } from "@ckpack/vue-color";
import { getAssetPath } from "@/utils/assetPath";
import { expandHexTo6 } from "@/utils/helpers/switchPixelsColor";
import { scrollbarClasses } from "@/utils/tailwindScrollbarClasses";

export default {
  setup() {
    return {
      getAssetPath,
      scrollbarClasses
    };
  },
  components: {
    ColorPicker: Chrome,
  },
  computed: {
    panelStyle() {
      const maxH = (typeof window !== "undefined" ? window.innerHeight : 800) * 0.64;
      const transformTransition = "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)";
      const heightTransition = this.panelHeight ? ", height 0.4s ease-out" : "";
      return {
        height: this.panelHeight ? `${this.panelHeight}px` : "auto",
        maxHeight: `${maxH}px`,
        transition: transformTransition + heightTransition,
      };
    },
  },
  watch: {
    showBackgroundPanel(val) {
      if (val) {
        this.$nextTick(() => {
          this.updatePanelHeight();
          if (this.$refs.panelContentRef && !this.resizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.updatePanelHeight());
            this.resizeObserver.observe(this.$refs.panelContentRef);
          }
        });
      }
    },
  },
  mounted() {
    if (this.showBackgroundPanel) {
      this.$nextTick(() => {
        this.updatePanelHeight();
        if (this.$refs.panelContentRef && !this.resizeObserver) {
          this.resizeObserver = new ResizeObserver(() => this.updatePanelHeight());
          this.resizeObserver.observe(this.$refs.panelContentRef);
        }
      });
    }
  },
  beforeUnmount() {
    if (this.resizeObserver && this.$refs.panelContentRef) {
      this.resizeObserver.unobserve(this.$refs.panelContentRef);
    }
  },
  props: {
    showBackgroundPanel: {
      required: true,
      type: Boolean,
    },
    showColorPicker: {
      required: true,
      type: Boolean,
    },
  },
  methods: {
    updatePanelHeight() {
      if (!this.$refs.panelContentRef || !this.showBackgroundPanel) return;
      const el = this.$refs.panelContentRef;
      const maxPx = (window.innerHeight * 64) / 100;
      this.panelHeight = Math.min(el.scrollHeight, maxPx);
    },
    isSameColor(a, b) {
      if (!a || !b) return false;
      const expand = (x) => {
        const str = (typeof x === "string" ? x : x?.hex ?? "").replace(/^#/, "").toLowerCase();
        if (str.length === 3) return str.split("").map((c) => c + c).join("");
        return str.length === 6 ? str : "";
      };
      return expand(a) === expand(b);
    },
    onPresetColorClick(col) {
      const normalized = expandHexTo6(col);
      this.color = normalized;
      this.$emit("onColorPicked", normalized);
    },
    onColorChange() {
      let c = this.color?.hex ?? this.color ?? "#";
      if (typeof c !== "string") c = "#";
      if (!c.startsWith("#")) c = "#" + c;
      this.color = expandHexTo6(c);
      this.$emit("onColorPicked", this.color);
    },
  },
  data() {
    return {
      panelHeight: 0,
      resizeObserver: null,
      activeTab: "patterns",
      color: "#FFFFFF",
      cardColors: [
        "#fff",
        "#000",
        "#1121AB",
        "#0B98FF",
        "#930BFF",
        "#FF0BC9",
        "#F5002C",
      ],
      patterns: [
        "https://cdn.pixabay.com/photo/2018/02/24/07/17/pattern-3177414_1280.png",
        "https://cdn.pixabay.com/photo/2017/01/31/17/55/background-2025984_1280.png",
        "https://cdn.pixabay.com/photo/2012/04/15/19/14/honeycomb-34984_1280.png",
        "https://cdn.pixabay.com/photo/2017/01/17/22/49/background-1988348_1280.png",
        "https://cdn.pixabay.com/photo/2017/02/15/20/33/floral-2069810_1280.png",
      ],
    };
  },
};
</script>

<style lang="scss">
#cardDesigner {
  .backgroundPanel {
    .fileButton {
      border: none !important;
    }

    .cardColor {
      width: 40px;
      height: 40px;
      border-radius: 50px;
      display: inline-block;
      border: 4px solid transparent;
      margin-right: 0.5rem;
      margin-top: 4px;
      margin-left: 4px;
    }

    .activeColor {
      border: 4px solid var(--color-sol-yellow);
    }

    .vc-chrome-saturation-wrap {
      padding-bottom: 40% !important;
    }
  }
}
</style>
